import { matchesBuiltinImage } from './builtin-resource-url'

function result (response) {
  const body = response && response.data
  if (!body || body.success === false) throw new Error(body && body.message || '图片资源返回格式不正确')
  return body.result === undefined ? body : body.result
}

export function imageCatalogs (response) {
  const data = result(response)
  if (!Array.isArray(data)) throw new Error('图片分类返回格式不正确')
  const records = data.filter(item => item && typeof item.id === 'string' && typeof item.name === 'string')
    .map(item => ({ id: item.id, name: item.name, parentId: item.parentId || '', children: [] }))
  const map = new Map(records.map(item => [item.id, item]))
  const roots = []
  records.forEach(item => {
    if (!item.parentId) roots.push(item)
    else if (map.has(item.parentId) && item.parentId !== item.id) map.get(item.parentId).children.push(item)
  })
  // 只从根节点输出，坏数据中的孤立目录或循环不会成为可选择的分类。
  return roots
}

export function imageUrl (value) {
  if (typeof value !== 'string' || value.length > 2048 || /[\s\\?#]/.test(value)) return ''
  const file = /\/file\/[A-Za-z0-9_-]{1,64}(?:\.(png|jpe?g|gif|webp|svg|bmp))?$/i.test(value)
  const builtin = /^\/(?:jetlinks\/)?visualization\/resource\/thumbnail\/[A-Za-z0-9_-]{1,64}\/0$/.test(value)
  if (!file && !builtin) return ''
  if (value.startsWith('/file/') || value.startsWith('/visualization/')) return '/jetlinks' + value
  if (value.startsWith('/') && !value.startsWith('//')) return value
  try {
    const url = new URL(value)
    return ['https:', 'http:'].includes(url.protocol) && !url.username && !url.password ? url.href : ''
  } catch (_) { return '' }
}

export function normalizeImage (item) {
  if (!item || !/^[A-Za-z0-9_-]{1,64}$/.test(item.id) || !Number.isInteger(item.version) || item.version < 1) return null
  const url = imageUrl(item.url)
  const visibility = item.visibility === undefined ? 'private' : item.visibility
  if (!['private', 'public'].includes(visibility)) return null
  const fileId = typeof item.fileId === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(item.fileId) ? item.fileId : ''
  const matchesFile = fileId && (new RegExp('/file/' + fileId + '(?:\\.[a-z]+)?$', 'i').test(url) || matchesBuiltinImage(url, item.id, fileId))
  return {
    id: item.id, name: String(item.name || '未命名图片').slice(0, 128), catalogId: item.catalogId,
    version: item.version, fileId, url,
    revision: Number.isSafeInteger(item.revision) && item.revision > 0 ? item.revision : null,
    description: typeof item.description === 'string' ? item.description.slice(0, 1000) : '',
    editable: item.editable === true,
    visibility,
    available: item.available === true && !!url && !!matchesFile,
    unavailableReason: String(item.unavailableReason || '图片文件暂不可用'),
    width: Number(item.width) > 0 ? Number(item.width) : null,
    height: Number(item.height) > 0 ? Number(item.height) : null
  }
}

export function imagePage (response) {
  const page = result(response)
  if (!page || !Array.isArray(page.data) || !Number.isInteger(page.total) || page.total < 0 ||
      !Number.isInteger(page.pageIndex) || page.pageIndex < 0 || !Number.isInteger(page.pageSize) || page.pageSize < 1 || page.pageSize > 48) {
    throw new Error('图片分页返回格式不正确')
  }
  const data = page.data.map(normalizeImage)
  if (data.some(item => !item) || data.length > page.pageSize || data.length > page.total) throw new Error('图片分页返回格式不正确')
  return { ...page, data }
}

export function imageError (error) {
  if (error && error.status === 404) return '图片资源接口尚未部署，请更新并重启后端后重试'
  return error && error.message || '图片资源加载失败，请重试'
}

export function uploadedImageFile (response) {
  const file = result(response)
  if (!file || typeof file.id !== 'string' || !/^[A-Za-z0-9_-]{1,64}$/.test(file.id)) {
    throw new Error('上传接口未返回有效文件ID')
  }
  return { id: file.id }
}

export function createdImage (response) {
  const image = normalizeImage(result(response))
  if (!image || !image.available) throw new Error('图片保存结果未确认，请刷新列表检查，勿重复提交')
  return image
}

export function editableImage (response) {
  const image = normalizeImage(result(response))
  if (!image || image.revision === null) throw new Error('图片编辑信息不完整，请更新后端后重试')
  if (!image.editable) throw new Error('当前账号无权编辑这张图片')
  return image
}

export function updatedImage (response, id, revision) {
  const image = editableImage(response)
  if (image.id !== id || image.revision !== revision + 1) {
    throw new Error('图片保存结果未确认，请刷新列表检查，勿重复提交')
  }
  return image
}

export function deletableImage (response, id) {
  const image = normalizeImage(result(response))
  if (!image || !image.editable || image.revision === null) throw new Error('当前图片不可删除，请确认归属及删除权限后刷新重试')
  if (image.id !== id || !Number.isSafeInteger(image.version) || !Number.isSafeInteger(image.revision + 1)) {
    throw new Error('图片删除信息不完整，请刷新列表并重新确认')
  }
  // 文件缺失不阻止从资源库隐藏，删除确认不需要上传或读取原图内容。
  return image
}

export function archivedImage (response, image) {
  const item = result(response)
  if (!item || item.id !== image.id || item.state !== 'archived' || item.version !== image.version ||
      item.revision !== image.revision + 1) throw new Error('图片删除结果未确认，请刷新列表检查，勿重复提交')
  return { id: item.id, version: item.version, revision: item.revision, state: item.state }
}

function restoreImageItem (item) {
  if (!item || typeof item.name !== 'string' || !item.name.trim() || item.name.length > 128 ||
      !/^[A-Za-z0-9_-]{1,64}$/.test(item.catalogId || '') ||
      (item.description != null && (typeof item.description !== 'string' || item.description.length > 1000)) ||
      [item.width, item.height].some(value => value != null && (!Number.isInteger(value) || value < 1))) return null
  return normalizeImage(item)
}

export function imageRestorePreview (response, id) {
  const data = result(response)
  const current = restoreImageItem(data && data.current), previous = restoreImageItem(data && data.previous)
  if (!current || !previous || current.id !== id || previous.id !== id || !current.editable || !previous.editable ||
      current.revision === null || current.revision !== previous.revision || current.version < 2 ||
      !Number.isSafeInteger(current.version + 1) || !Number.isSafeInteger(current.revision + 1) ||
      previous.version !== current.version - 1 || previous.visibility !== current.visibility) throw new Error('图片上一版信息不完整，请刷新列表并重新读取')
  if (!previous.available) throw new Error('上一版图片文件不可用，暂时无法恢复')
  return { current, previous }
}

export function restoredImage (response, preview) {
  const image = restoreImageItem(result(response)), previous = preview.previous
  if (!image || !image.editable || image.id !== preview.current.id || image.version !== preview.current.version + 1 ||
      image.revision !== preview.current.revision + 1 || !image.available || image.fileId !== previous.fileId ||
      image.name !== previous.name || image.catalogId !== previous.catalogId || image.description !== previous.description ||
      image.width !== previous.width || image.height !== previous.height || image.visibility !== preview.current.visibility) {
    throw new Error('图片恢复结果未确认，请刷新列表检查，勿重复提交')
  }
  return image
}

export function createdImageCatalog (response) {
  const catalog = result(response)
  if (!catalog || typeof catalog.id !== 'string' || !/^[A-Za-z0-9_-]{1,64}$/.test(catalog.id)
      || typeof catalog.name !== 'string' || !catalog.name.trim()) throw new Error('分类保存结果未确认，请刷新分类检查')
  return { id: catalog.id, name: catalog.name, parentId: catalog.parentId || '' }
}

export function imageUploadError (file) {
  if (!file || !Number.isFinite(file.size) || file.size < 1 || file.size > 20 * 1024 * 1024) return '请选择20 MB以内的图片'
  if (!/\.(png|jpe?g|webp|gif)$/i.test(file.name)
      || (file.type && !['image/png', 'image/jpeg', 'image/webp', 'image/gif'].includes(file.type))) {
    return '仅支持PNG、JPG、WebP或GIF，暂不开放SVG上传'
  }
  return ''
}
